import * as XLSX from 'xlsx';
import type { ExportData, ExportOptions } from './types';

/**
 * Converts ExportData to XLSX worksheet format
 * @param data - Data with headers and rows
 * @param options - Export options
 * @returns XLSX worksheet
 * @example
 * const ws = createWorksheet({
 *   headers: ['Name', 'Age'],
 *   rows: [['John', 25], ['Jane', 30]]
 * })
 */
function createWorksheet(data: ExportData, options?: ExportOptions): XLSX.WorkSheet {
  const includeHeaders = options?.includeHeaders !== false;

  const worksheetData: (string | number | boolean | null)[][] = [];

  if (includeHeaders && data.headers.length > 0) {
    worksheetData.push(data.headers);
  }

  worksheetData.push(...data.rows);

  return XLSX.utils.aoa_to_sheet(worksheetData);
}

/**
 * Exports data to XLSX file and triggers download
 * @param data - Data to export
 * @param options - Export configuration including filename and sheetName
 * @example
 * exportToXLSX({
 *   headers: ['Product', 'Price', 'Stock'],
 *   rows: [['Laptop', 999, 15], ['Mouse', 29, 100]]
 * }, {
 *   filename: 'inventory.xlsx',
 *   sheetName: 'Products'
 * })
 */
export function exportToXLSX(data: ExportData, options?: ExportOptions): void {
  try {
    const worksheet = createWorksheet(data, options);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, options?.sheetName || 'Data');

    XLSX.writeFile(workbook, options?.filename || 'export.xlsx');
  } catch (error) {
    console.error('Failed to export XLSX:', error);
    throw new Error('Failed to export data to XLSX');
  }
}

/**
 * Generates XLSX buffer without downloading
 * @param data - Data to convert
 * @param options - Export configuration
 * @returns ArrayBuffer with XLSX data
 * @example
 * const buffer = generateXLSX({
 *   headers: ['Name', 'Email'],
 *   rows: [['John', 'john@example.com']]
 * })
 */
export function generateXLSX(data: ExportData, options?: ExportOptions): ArrayBuffer {
  const worksheet = createWorksheet(data, options);
  const workbook = XLSX.utils.book_new();

  XLSX.utils.book_append_sheet(workbook, worksheet, options?.sheetName || 'Data');

  return XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
}

/**
 * Exports multiple sheets to XLSX file
 * @param sheets - Array of sheet data with names
 * @param filename - Output filename
 * @example
 * exportMultiSheetXLSX([
 *   {
 *     name: 'Users',
 *     data: {
 *       headers: ['Name', 'Email'],
 *       rows: [['John', 'john@example.com']]
 *     }
 *   },
 *   {
 *     name: 'Products',
 *     data: {
 *       headers: ['Product', 'Price'],
 *       rows: [['Laptop', 999]]
 *     }
 *   }
 * ], 'report.xlsx')
 */
export function exportMultiSheetXLSX(
  sheets: Array<{ name: string; data: ExportData; options?: ExportOptions }>,
  filename: string
): void {
  const workbook = XLSX.utils.book_new();

  sheets.forEach(({ name, data, options }) => {
    const worksheet = createWorksheet(data, options);
    XLSX.utils.book_append_sheet(workbook, worksheet, name);
  });

  XLSX.writeFile(workbook, filename);
}
